import { User } from "./user";
import type { DerivedDatasetInfo } from './dataAnalyst';
import { RegistrationSource } from "../enums/registrationSource";
import type { Environment } from "./environment";

export interface ToolFilterPreset {
  id: string;
  name: string;
  filters: {
    statusFilter?: string;
    methodFilter?: string;
    environmentFilter?: string;
    connectionFilter?: string[] | null;
    selectedTags?: string[];
    sortBy?: string;
    searchTerm?: string;
  };
  createdAt: string;
}

export interface UsersTabProps {
  showCreateUser: boolean;
  setShowCreateUser: (show: boolean) => void;
  getRoleBadge: (role: number) => React.ReactNode;
}

export type CreateUserDto = {
  email: string;
  password?: string | null;
  confirmPassword?: string | null;
  first_name: string;
  last_name: string;
  role?: number;
  registrationSource: RegistrationSource;
}

export interface ApiKey {
  id: number
  name: string
  key_prefix: string
  scopes: string[]
  is_active: boolean
  expires_at?: string
  last_used?: string
  usage_count: number
  created_at: string
}

export interface EditUserModalProps {
  editingUser: User | null;
  setEditingUser: (user: User | null) => void;
  editUserForm: any;
  setEditUserForm: (form: any) => void;
  currentUser: User | null;
}

export interface McpTool {
  id: number;
  name: string;
  title?: string;
  description: string;
  inputSchema?: object;
  outputSchema?: object;
  annotations?: object;
  tags?: Tag[];
  enabled: boolean;
  version: string;
  httpMethod: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'SELECT';
  path?: string;
  queryMap?: object;
  headerMap?: object;
  bodyTemplate?: string;
  responseTemplate?: string;
  sampleResponse?: string;
  mainConnectionId?: string;
  subConnectionsIds?: string[];
  environment?: Environment;
  created_at?: string | Date;
  updated_at?: string | Date;
  formEnabled?: boolean;
}

// New Connection Config DTOs aligned with backend entity
export interface ConnectionConfigCreateDto {
  connectionName?: string;
  type?: string; // default 'api' if omitted
  auth_type: string;
  idpProviderId?: string;
  config: Record<string, any>;
  auth_fields?: Record<string, any>;
  description?: string;
  isActive?: boolean;
  httpProxy?: string;
  defaultHeaders?: Record<string, string>;
  environment?: Environment;
}

export interface ConnectionConfigResponseDto {
  id: string; // uuid
  connectionName: string;
  type: string;
  auth_type: string;
  idpProviderId?: string;
  config: Record<string, any>;
  auth_fields?: Record<string, any>;
  description?: string;
  isActive: boolean;
  httpProxy?: string;
  defaultHeaders?: Record<string, string>;
  environment?: Environment;
  createdAt: string;
  updatedAt: string;
}

export interface ConnectionTestBulkResult {
  id: string;
  success: boolean;
  status?: number;
  message?: string;
}

export interface ConnectionConfig {
  id: string;
  connectionName: string;
  type: string;
  authType: string;
  idpProviderId?: string;
  config: Record<string, any>;
  authFields?: Record<string, any>;
  description?: string;
  isActive: boolean;
  isBase?: boolean;
  httpProxy?: string;
  defaultHeaders?: Record<string, string>;
  connectionTypeId: number;
  environment?: Environment;
  createdAt: string;
  updatedAt: string;
}


export interface Tag {
  id?: number
  name: string
  count?: number
}

export type IdpProviderType = {
  id: string;
  type: string;
  name: string;
  description?: string;
  issuerUrl?: string;
  clientId?: string;
  clientSecret?: string;
  hasClientSecret?: boolean;
  authorizationUrl?: string;
  tokenUrl?: string;
  userInfoUrl?: string;
  config?: Record<string, any>;
  active: boolean;
  createdAt: Date;
  updatedAt: Date;
};

export type CreateIdpProviderDto = {
  type: string;
  name: string;
  description?: string;
  issuerUrl?: string;
  clientId?: string;
  clientSecret?: string;
  authorizationUrl?: string;
  tokenUrl?: string;
  config?: Record<string, any>;
};

export type UpdateIdpProviderDto = Partial<CreateIdpProviderDto>;
export type ToolDisplayStatus = 'in_progress' | 'missing' | 'disabled' | 'enabled';

export interface BasicConnector {
  id: string;
  isActive: boolean;
  connectionName: string;
  environment?: Environment;
  connectionTypeId: number;
}

export interface ToolOption {
  id: string;
  name: string;
  description?: string;
  connectorId?: string;
  connectorName?: string;
  environment?: string;
  httpMethod?: string;
  tags?: string[];
}

export interface ToolPickerProps {
  options: ToolOption[];
  selected: ToolOption[];
  onChange: (selected: ToolOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

export interface SafeProvider {
  id: string,
  name: string,
  type: string,
  active: boolean,
} 

export type SettingsTabId =
  | 'users'
  | 'api-keys'
  | 'sso'
  | 'logs'
  | 'general'
  | 'llm-settings'
  | 'chat-llm'
  | 'chat-voice'
  | 'packages'
  | 'system'
  | 'prompts'
  | 'ai-builder'
  | 'data-lake';

export const SETTINGS_TABS: SettingsTabId[] = [
  'users',
  'api-keys',
  'sso',
  'logs',
  'general',
  'llm-settings',
  'chat-llm',
  'chat-voice',
  'packages',
  'system',
  'prompts',
  'ai-builder',
  'data-lake',
];

export interface DatasetSchemaColumn {
  name: string;
  type: string;
}

export interface DatasetSnapshotItem {
  datasetId: string;
  toolName: string;
  arrayKey: string;
  rowCount: number;
  columnCount: number;
  memoryBytes: number;
  expiresAt: string;
  schema: DatasetSchemaColumn[];
  paramsSummary?: string;
  derived?: DerivedDatasetInfo;
}

export interface SessionSnapshotItem {
  sessionId: string;
  lastActivityAt: string;
  memoryBytes: number;
  datasets: DatasetSnapshotItem[];
}

export interface DataLakeStatusResponse {
  totalMemoryBytes: number;
  sessions: SessionSnapshotItem[];
}

export interface DatasetRowsResponse {
  rows: Record<string, unknown>[];
  rowCount: number;
  totalRows: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DataLakeQueryResponse {
  dataset_id: string;
  sql: string;
  columns: DatasetSchemaColumn[];
  rows: Record<string, unknown>[];
  row_count: number;
  duration_ms: number;
}

export interface PromptTemplateVariable {
  name: string;
  description: string;
}

export interface PromptTemplate {
  id: number;
  key: string;
  label: string;
  description: string | null;
  variables: string | null;
  content: string;
  /** Grouping in the Prompts tab - set by the server for built-in templates; missing/"Custom" for user-created ones. */
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromptTemplateDto {
  key: string;
  label: string;
  description?: string;
  variables?: PromptTemplateVariable[];
  content: string;
}
export interface AgentFeedback {
  iteration: number;
  maxIterations: number;
  validationErrors: string[];
  validationWarnings: string[];
  testRan: boolean;
  testSuccess: boolean;
  testSkippedReason?: string;
  testError?: string;
  testErrorCategory?: string;
  testRequestUrl?: string;
  testResponseBody?: string;
  askUserForClarification?: boolean;
}

export interface ToolConfigForAgent {
  name?: string;
  title?: string;
  description?: string;
  httpMethod?: string;
  path?: string;
  inputSchema?: Record<string, unknown>;
  queryMap?: Array<{ key: string; value: string; dynamic?: boolean; selected?: boolean }>;
  headerMap?: Record<string, string>;
  bodyTemplate?: string;
  responseTemplate?: string;
  mainConnectionId?: string;
  annotations?: Record<string, unknown>;
  sampleResponse?: string;
  version?: string;
  enabled?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export interface TestResult {
  ran: boolean;
  success: boolean;
  skippedReason?: string;
  data?: unknown;
  error?: string;
  category?: string;
  statusCode?: number;
  connectorName?: string;
  requestUrl?: string;
  responsePreview?: string;
}

export interface StartAgentRunDto {
  mode: 'new' | 'edit';
  toolTitle?: string;
  currentValues?: Record<string, unknown>;
  selectedConnectorId?: string;
  connectorTypeId?: number;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  skillsBlock?: string;
  toolId?: number | null;
  lastTestResponse?: string;
}

export interface StartAgentRunResponse {
  runId: string;
  ticket: string;
}
export interface Log {
    id: number;
    message: string;
    level: string;
    source?: string;
    context?: string;
    timestamp: string | Date;
    min_visibility_role_id?: number;
    user_id?: string | null;
    user_email?: string | null;
}

export interface LogUserOption {
    id: string;
    email: string | null;
}
